import { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import type { RuleLevel, RuleCondition, RuleAction } from '../../api/orderLogicRules';

interface RuleLevelEditorProps {
  level: RuleLevel;
  onChange: (level: RuleLevel) => void;
  depth?: number;
}

const CONDITION_OPERATORS = [
  'equals', 'not_equals', 'greater_than', 'less_than',
  'greater_than_or_equals', 'less_than_or_equals',
  'contains', 'not_contains', 'in', 'not_in', 'between',
  'exists', 'not_exists', 'starts_with', 'ends_with',
];

const CONDITION_SOURCES: Array<'order' | 'user' | 'response'> = ['order', 'user', 'response'];

function emptyCondition(): RuleCondition {
  return { field: '', operator: 'equals', value: '', source: 'order' };
}

function emptyAction(): RuleAction {
  return { type: 'create_order', config: { template: {} } };
}

export default function RuleLevelEditor({ level, onChange, depth = 0 }: RuleLevelEditorProps) {
  const [expanded, setExpanded] = useState(true);
  const [showElse, setShowElse] = useState(!!level.elseLevel);

  const updateCondition = (idx: number, cond: RuleCondition) => {
    const conditions = [...level.conditions];
    conditions[idx] = cond;
    onChange({ ...level, conditions });
  };

  const addCondition = () => {
    onChange({ ...level, conditions: [...level.conditions, emptyCondition()] });
  };

  const removeCondition = (idx: number) => {
    onChange({ ...level, conditions: level.conditions.filter((_, i) => i !== idx) });
  };

  const updateAction = (idx: number, action: RuleAction) => {
    const actions = [...level.actions];
    actions[idx] = action;
    onChange({ ...level, actions });
  };

  const addAction = () => {
    onChange({ ...level, actions: [...level.actions, emptyAction()] });
  };

  const removeAction = (idx: number) => {
    onChange({ ...level, actions: level.actions.filter((_, i) => i !== idx) });
  };

  return (
    <div className={clsx('border rounded-lg p-4', depth === 0 ? 'border-gray-200 dark:border-gray-700' : 'border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-900/10')}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Level {depth + 1}
        </button>
        <select
          className="input w-auto text-xs py-1"
          value={level.operator}
          onChange={(e) => onChange({ ...level, operator: e.target.value as 'AND' | 'OR' })}
        >
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
      </div>

      {expanded && (
        <>
          {/* Conditions */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Conditions</h4>
              <button type="button" onClick={addCondition} className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-0.5">
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {level.conditions.map((cond, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-100 dark:border-gray-700">
                  <select
                    className="input text-xs py-1 w-20"
                    value={cond.source}
                    onChange={(e) => updateCondition(idx, { ...cond, source: e.target.value as 'order' | 'user' | 'response' })}
                  >
                    {CONDITION_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <input
                    type="text"
                    className="input text-xs py-1 flex-1"
                    placeholder="field (e.g. request.payload.amount)"
                    value={cond.field}
                    onChange={(e) => updateCondition(idx, { ...cond, field: e.target.value })}
                  />
                  <select
                    className="input text-xs py-1 w-36"
                    value={cond.operator}
                    onChange={(e) => updateCondition(idx, { ...cond, operator: e.target.value })}
                  >
                    {CONDITION_OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
                  </select>
                  <input
                    type="text"
                    className="input text-xs py-1 flex-1"
                    placeholder="value"
                    value={typeof cond.value === 'string' ? cond.value : JSON.stringify(cond.value)}
                    onChange={(e) => {
                      let val: unknown = e.target.value;
                      try { val = JSON.parse(e.target.value); } catch { /* keep as string */ }
                      updateCondition(idx, { ...cond, value: val });
                    }}
                  />
                  <button type="button" onClick={() => removeCondition(idx)} className="text-gray-400 hover:text-red-500 dark:hover:text-red-400">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Actions</h4>
              <button type="button" onClick={addAction} className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-0.5">
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {level.actions.map((action, idx) => (
                <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      Action: {action.type}
                    </span>
                    <button type="button" onClick={() => removeAction(idx)} className="text-gray-400 hover:text-red-500 dark:hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <textarea
                    className="input font-mono text-xs h-20"
                    value={JSON.stringify(action.config, null, 2)}
                    onChange={(e) => {
                      try {
                        updateAction(idx, { ...action, config: JSON.parse(e.target.value) });
                      } catch { /* ignore invalid JSON while typing */ }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Else Level */}
          <div>
            {!showElse ? (
              <button
                type="button"
                onClick={() => {
                  setShowElse(true);
                  onChange({
                    ...level,
                    elseLevel: { operator: 'AND', conditions: [], actions: [] },
                  });
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                + Add Else Branch
              </button>
            ) : (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Else Branch</h4>
                  <button
                    type="button"
                    onClick={() => {
                      setShowElse(false);
                      const { elseLevel: _, ...rest } = level;
                      onChange(rest as RuleLevel);
                    }}
                    className="text-xs text-red-500 dark:text-red-400 hover:underline"
                  >
                    Remove
                  </button>
                </div>
                <RuleLevelEditor
                  level={level.elseLevel!}
                  onChange={(el) => onChange({ ...level, elseLevel: el })}
                  depth={depth + 1}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
